import {
  useState, useCallback, useRef, FC, MutableRefObject,
} from 'react';
import {
  DataGridPro, GridApiPro, GridColDef,
  useGridApiRef,
} from '@mui/x-data-grid-pro';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Card from '@mui/material/Card';
import { useTheme } from '@mui/material';
import { useDispatch } from 'react-redux';
import Stack from '@mui/material/Stack';
import SvgIcon from '@mui/material/SvgIcon';
import moment from 'moment-timezone';
import Chip from '@mui/material/Chip';
import { FormProvider, useForm } from 'react-hook-form';
import startCase from 'lodash/startCase';
import LoadingButton from '@mui/lab/LoadingButton';
import { useTranslation } from 'react-i18next';
import { TCustomTheme } from '@mgm/common/theme';
import { useCustomTheme } from '@mgm/common/hooks/useCustomTheme';
import { Iconify, CustomNoRowsOverlay } from '@mgm/common/components';
import { useScreenResize } from '@mgm/common/hooks/useResize';
import { BaseLabel, BaseTooltip, BaseChip } from '@mgm/common/components/base';
import { BASE_COLORS, convertToCamelCase } from '@mgm/common/utils';
import { useNavigate } from 'react-router-dom';
import Button from '@mui/material/Button';
import { usePostHog } from 'posthog-js/react';
import Divider from '@mui/material/Divider';
import { useQueryClient } from '@tanstack/react-query';
import { keyframes } from '@emotion/react';
import { PrintWrapper } from '../../../layout/wrapper/PrintWrapper';
import { useAppSelector } from '../../../hooks/useAppStore';
import { getIsPrintMode, getPlatformData } from '../../../store/common/selectors';
import { OrdersCountLoader, OrdersTableHeader } from '../Shared/components';
import { useTable } from '../../../hooks/useTable';
import {
  useAssignMappingsAPI,
  useOrders,
  useReassignMappingAPI,
} from '../Shared/hooks';
import {
  getAttributedOrders,
} from '../../../store/orders/selectors';
import { ordersActions } from '../../../store/orders/slice';
import { STATUS_COLOR_MAP } from '../Shared/data/orders';
import {
  AvatarSelect, MemoizedFilterTabs,
} from '../../../components/base';
import {
  TAssignOrder, TCommissionPlan, TMember, TOrderMapping,
} from '../../../types';
import { ReactComponent as NavigateIcon } from '../../../static/icons/ic_navigate.svg';

import { TCreateEditForm } from '../../../components/form';
import { useOrderPermissions } from '../Shared/hooks/useOrderPermissions';
import { getCommissionTeams } from '../../../store/commissions/selectors';
import { useAttributedOrdersOrdersFilter } from './hooks/useAttributedOrdersOrdersFilter';
import { getMembers } from '../../../store/team/selectors';
import { useCommon, usePermissions } from '../../../hooks/useCommon';
import { useGetSheetsAPI } from '../../../hooks/api/useSheets';
import { useGetOrdersCountAPI } from '../../../hooks/api/useOrders';
import { INDEX_TO_COLOR } from '../../../data';
import { AssignShopifyIdDialog } from './components/AssignShopifyIdDialog';
import { getAuthenticateUser, getTokenDetails } from '../../../store/authenticate/selectors';
import { useCommonOrders } from '../../../hooks/useCommonOrders';
import { memberActions } from '../../../store/team/slice';
import { QUERY_KEYS } from '../../../utils/enums';

type TOrdersList = {
  columnVisibilityModel: { [key: string]: boolean }
};

const blinkAnimation = keyframes`
  0% { opacity: 1; }
  50% { opacity: 0; }
  100% { opacity: 1; }
`;

const OrdersList: FC<TOrdersList> = ({ columnVisibilityModel }) => {
  const dispatch = useDispatch();
  const apiRef = useGridApiRef() as MutableRefObject<GridApiPro>;
  const componentRef = useRef<HTMLDivElement | null>(null);
  const theme = useTheme() as TCustomTheme;
  const { isMobile } = useCustomTheme(theme);
  const isPrintMode = useAppSelector(getIsPrintMode);
  const tokenDetails = useAppSelector(getTokenDetails);

  const {
    localText,
    handleScrollTable,
    handleExport,
    exporting,
  } = useTable(apiRef);

  const { pageHeight, resizableDiv, isDesktopSmallScreen } = useScreenResize(isMobile ? 20 : 50);
  const { t } = useTranslation(['inputs', 'orders', 'tasklists', 'commissions', 'common', 'timesheets', 'team']);
  const { currencies, locations } = useAppSelector(getPlatformData);
  const { ordersPermissions } = useOrderPermissions();
  const attributedOrders = useAppSelector(getAttributedOrders);
  const { permissions } = usePermissions();
  const members = useAppSelector(getMembers);
  const commissionTeams = useAppSelector(getCommissionTeams);
  const navigate = useNavigate();
  const { locationsByPermissions } = useCommon();
  const { shop, personnel } = useAppSelector(getAuthenticateUser);
  const posthog = usePostHog();
  const [isOpenAssignDialog, setIsOpenAssignDialog] = useState(false);
  const [updatedOrder, setUpdatedOrder] = useState({} as TAssignOrder);

  const [changes, setChanges] = useState<{ [key: string]: {
    memberChanged: boolean;
    commissionPlanChanged: boolean, prevMember: TMember } }>({});

  const [isFullTable, setIsFullTable] = useState(false);

  const { getAttributedOrdersByFilters } = useAttributedOrdersOrdersFilter();

  const queryClient = useQueryClient();

  const isOrdersInCache = !!queryClient.getQueryData([QUERY_KEYS.ORDERS.GET_ORDERS]);

  const { setIsFetchByDate, ordersMapping } = useCommonOrders({
    prevTab: isOrdersInCache ? '2' : '',
  });

  const methods = useForm<TCreateEditForm>({
    mode: 'all',
  });

  const { getOrderData } = useOrders();

  useGetSheetsAPI({
    isMainSheetsPage: true,
    byWeek: true,
    timezone: shop.shopTimezone,
  });

  useGetOrdersCountAPI({
    isMainPage: true,
  });

  const { mutateAsync: reassignMappingMutate, isLoading: isReassignMappingLoading } = useReassignMappingAPI();
  const { mutateAsync: assignMappingMutate, isLoading: isAssignMappingLoading } = useAssignMappingsAPI();

  const getLengthByStatus = (locationId: string | number) => attributedOrders.filter(
    (order) => order.locationId === locationId,
  ).length;

  const tabs = locationsByPermissions.map((location, index) => ({
    value: location.id,
    label: location.name,
    color: INDEX_TO_COLOR[index],
    count: getLengthByStatus(location.id),
  }));

  tabs.unshift({
    value: 0,
    label: t('all', { ns: 'common' }),
    color: 'info',
    count: attributedOrders?.length,
  });

  const handleFilterStatus = (newValue: number) => {
    dispatch(ordersActions.setSelectedLocationId(newValue));
  };

  const handleMemberChange = (
    orderId: string,
    newMember: TMember,
    prevMember: TMember,
  ) => {
    // Check if the new member is part of a team
    const isCommissionTeam = commissionTeams.some((team) => team.members.some((member) => member.email === newMember.email));

    const formattedOrders = attributedOrders.map((order) => (String(order.id) !== String(orderId)
      ? order
      : {
        ...order,
        userId: newMember.shopifyId,
        member: newMember,
        isCommissionTeam,
      }));

    dispatch(ordersActions.setAttributedOrders(formattedOrders));

    setChanges((prevState) => ({
      ...prevState,
      [orderId]: {
        ...prevState[orderId],
        memberChanged: true,
        prevMember,
        isCommissionTeam,
      },
    }));
  };

  const renderTableHeader = useCallback(() => (
    <OrdersTableHeader
      printRef={componentRef}
      handleScrollTable={handleScrollTable}
      apiRef={apiRef}
      setIsFetchByDate={setIsFetchByDate}
      isFullTable={isFullTable}
      setIsFullTable={setIsFullTable}
      handleExport={() => handleExport('mgm-orders')}
    />
  ), [apiRef, isFullTable]);

  const checkIfOrderExists = (firstOrder: TAssignOrder, otherOrders: Array<TOrderMapping>) => {
    const orderIdToCheck = String(firstOrder.id); // Convert id to string if necessary

    // eslint-disable-next-line no-restricted-syntax
    for (const orderGroup of otherOrders) {
      const orderExists = orderGroup.orders.some((order) => String(order.orderId) === orderIdToCheck);
      if (orderExists) {
        return true;
      }
    }

    return false;
  };

  const handleSave = async (orderId: string) => {
    const orderChanges = changes[orderId];

    if (orderChanges) {
      const isValidEmail = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(orderChanges.prevMember?.email);

      if (orderChanges.memberChanged) {
        const updatedOrder = attributedOrders.find((order) => String(order.id) === String(orderId));

        setUpdatedOrder(updatedOrder as TAssignOrder);
        const orderExists = checkIfOrderExists(updatedOrder as TAssignOrder, ordersMapping);

        if (!orderExists || !isValidEmail) {
          if (!updatedOrder?.member?.email) setIsOpenAssignDialog(true);
          const ordersData = getOrderData(attributedOrders);

          const data = {
            orders: ordersData,
            monthYear: moment.tz(updatedOrder?.processedAt, shop.shopTimezone).format('YYYY-MM'),
          };

          await assignMappingMutate(data);
        } else {
          const data = {
            orderId: String(updatedOrder?.id) || '',
            monthYear: moment.tz(updatedOrder?.processedAt, shop.shopTimezone).format('YYYY-MM'),
            previousEmail: orderChanges.prevMember.email,
            mapping: {
              email: updatedOrder?.member?.email || '',
              team: '',
              commissionPlan: updatedOrder?.commissionPlan || {} as TCommissionPlan,
            },
          };

          await reassignMappingMutate(data);
        }
      }

      setChanges((prevState) => ({
        ...prevState,
        [orderId]: {
          memberChanged: false,
          commissionPlanChanged: false,
          prevMember: {} as TMember,
        },
      }));
    }
  };

  const openProductExchanges = (orderId: string) => {
    const updatedOrder = attributedOrders.find((order) => String(order.id) === String(orderId));

    dispatch(ordersActions.setSelectedOrder(updatedOrder as TAssignOrder));
    navigate(`/orders/:${orderId}`);
  };

  const renderCustomNoRowsOverlay = () => <CustomNoRowsOverlay text={t('itLooksLikeThereAreNoOrders', { ns: 'orders' })} />;

  const orderColumns: Array<GridColDef> = [
    {
      field: 'name',
      headerName: t('invoiceId', { ns: 'orders' }),
      width: 170,
      renderCell: ({ row }) => (
        <Stack direction="row" spacing={1} alignItems="center" height="100%">
          <Typography variant="caption">
            {row.name}
          </Typography>
        </Stack>
      ),
      valueGetter: (_, row) => row.name,
    },
    {
      field: 'id',
      headerName: t('orderId', { ns: 'orders' }),
      width: 170,
      renderCell: ({ row }) => (
        <Stack direction="row" spacing={1} alignItems="center" height="100%">
          <Typography variant="caption">
            {row.id}
          </Typography>
          {permissions.isAdmin && (
          <Box
            sx={{
              cursor: 'pointer',
            }}
            onClick={() => {
              window.open(row.orderStatusUrl, '_blank');
            }}
          >
            <SvgIcon
              sx={{
                fontSize: 16,
                fill: '#A855F7',
              }}
              component={NavigateIcon}
              inheritViewBox
            />
          </Box>
          )}
        </Stack>
      ),
      valueGetter: (_, row) => row.id,
    },
    {
      field: 'processedAt',
      headerName: t('date', { ns: 'orders' }),
      width: 200,
      renderCell: ({ row }) => (
        <Box display="flex" alignItems="center" height="100%">
          <Typography variant="caption">
            {moment.tz(row.processedAt, shop.shopTimezone).format('DD MMMM, hh:mm A')}
          </Typography>
        </Box>
      ),
      valueFormatter: (params) => {
        if (params) {
          return moment.tz(params, shop.shopTimezone).format('DD MMMM, hh:mm A');
        }

        return '';
      },
    },
    {
      field: 'locationId',
      headerName: t('location'),
      width: 170,
      renderCell: ({ row }) => {
        const location = locations.find(({ id }) => String(id) === String(row.locationId));

        return (
          <Box display="flex" alignItems="center" height="100%">
            <Typography variant="caption">
              {location?.name || '/'}
            </Typography>
          </Box>
        );
      },
      valueGetter: (_, row) => {
        const location = locations.find(({ id }) => String(id) === String(row.locationId));

        return location?.name || '/';
      },
    },
    {
      field: 'tags',
      headerName: t('commissionTags', { ns: 'team' }),
      minWidth: 200,
      renderCell: ({ row }) => (
        row.tags?.length
          ? row.tags?.map((tag: string, index: number) => (
            <BaseChip
              color="#A855F7"
              key={`${tag}-${index.toString()}`}
              label={tag}
              size="small"
              isDark={tokenDetails?.settings?.ui?.theme === 'dark'}
            />
          )) : (
            <Typography variant="caption">
              /
            </Typography>
          )
      ),
      valueGetter: (_, row) => {
        if (row?.tags?.length > 0) {
          return row?.tags?.join(' ,');
        }
        return '/';
      },
    },
    {
      field: 'subTotal',
      headerName: t('netSales', { ns: 'commissions' }),
      width: 170,
      renderCell: ({ row }) => {
        const currency = currencies.find(({ code }) => code === row.currency);

        const netSales = row.summary.netSales ? Number(row.summary.netSales)?.toFixed(2) : 0;
        return (
          <Box display="flex" alignItems="center" height="100%">
            <Typography variant="caption">
              {`${currency?.mark} ${netSales}`}
            </Typography>
          </Box>
        );
      },
      valueGetter: (_, row) => {
        const currency = currencies.find(({ code }) => code === row.currency);

        const netSales = row.summary.netSales ? Number(row.summary.netSales)?.toFixed(2) : 0;

        return `${currency?.mark} ${netSales}`;
      },
    },
    {
      field: 'discounts',
      headerName: t('discounts', { ns: 'orders' }),
      width: 170,
      renderCell: ({ row }) => {
        const currency = currencies.find(({ code }) => code === row.currency);

        const discount = row.summary?.discounts ? row.summary.discounts : '0.0';

        return (
          <Box display="flex" alignItems="center" height="100%">
            <Typography variant="caption">
              {`${currency?.mark} ${discount}`}
            </Typography>
          </Box>
        );
      },
      valueGetter: (_, row) => {
        const currency = currencies.find(({ code }) => code === row.currency);

        const discount = row.summary?.discounts ? row.summary.discounts : '0.0';

        return `${currency?.mark} ${discount}`;
      },
    },
    {
      field: 'tips',
      headerName: 'Tips',
      width: 170,
      renderCell: ({ row }) => {
        const currency = currencies.find(({ code }) => code === row.currency);

        const tips = row.summary?.tips ? row.summary.tips : '0.0';

        return (
          <Box display="flex" alignItems="center" height="100%">
            <Typography variant="caption">
              {`${currency?.mark} ${tips}`}
            </Typography>
          </Box>
        );
      },
      valueGetter: (_, row) => {
        const currency = currencies.find(({ code }) => code === row.currency);

        const tips = row.summary?.tips ? row.summary.tips : '0.0';

        return `${currency?.mark} ${tips}`;
      },
    },
    {
      field: 'commission',
      width: 170,
      hideSortIcons: true,
      renderHeader: () => (
        <Box id="table-commissions" width="100%" display="flex" alignItems="center" height="100%">
          <Typography variant="subtitle2">
            {t('commission', { ns: 'commissions' })}
          </Typography>
          <BaseTooltip tooltipText={t('calculatedPerSale', { ns: 'commissions' })}>
            <Box pt={0.5} pl={0.5}>
              <Iconify icon="cuida:help-outline" width={16} />
            </Box>
          </BaseTooltip>
        </Box>
      ),
      renderCell: ({ row }) => {
        const currency = currencies.find(({ code }) => code === row.currency);

        const commission = row.commission ? Number(row.commission)?.toFixed(2) : '0.0';

        return (
          <Box display="flex" alignItems="center" height="100%">
            <Typography variant="caption">
              {`${currency?.mark} ${commission}`}
            </Typography>
          </Box>
        );
      },
      valueGetter: (_, row) => {
        const currency = currencies.find(({ code }) => code === row.currency);

        const commission = row.commission ? Number(row.commission)?.toFixed(2) : '0.0';

        return `${currency?.mark} ${commission}`;
      },
    },
    {
      field: 'sourceName',
      headerName: t('sourceName', { ns: 'commissions' }),
      width: 170,
      renderCell: ({ row }) => (
        <Box display="flex" alignItems="center" height="100%">
          <Typography variant="caption">
            {row.sourceName}
          </Typography>
        </Box>
      ),
    },
    {
      field: 'financialStatus',
      headerName: t('status'),
      width: 120,
      renderCell: ({ row }) => {
        const currency = currencies.find(({ code }) => code === row.currency);

        const refundsTotal = row.summary.refundsTotal ? Number(row.summary.refundsTotal)?.toFixed(2) : '0.0';

        return (
          <Box display="flex" alignItems="center" height="100%">
            <BaseTooltip
              isDisabled={row.financialStatus === 'paid'}
              tooltipText={`${t('totalRefunds', { ns: 'commissions' })}: ${currency?.mark} ${refundsTotal}`}
            >
              <Chip
                size="small"
                label={t(`${convertToCamelCase(row.financialStatus)}`, { ns: 'tasklists' })}
                color={STATUS_COLOR_MAP[row.financialStatus]}
                variant="outlined"
              />
            </BaseTooltip>
          </Box>
        );
      },
      valueGetter: (_, row) => t(`${convertToCamelCase(row.financialStatus)}`, { ns: 'tasklists' }),
    },
    {
      field: 'commissionPlan',
      headerName: t('commissionPlan', { ns: 'orders' }),
      width: 225,
      renderCell: ({ row }) => {
        const isProductCommission = row.commissionPlan?.type === 'Products';

        // eslint-disable-next-line no-nested-ternary
        const planIcon = isProductCommission
          ? 'fluent-mdl2:product-variant'
          : row.commissionPlan?.type === 'Fixed'
            ? 'mdi:lock-rate-outline'
            : 'lucide-lab:stairs-arrow-up-right';

        // eslint-disable-next-line no-nested-ternary
        const planColor = isProductCommission
          ? '#4a599f'
          : row.commissionPlan?.type === 'Fixed'
            ? '#A855F7'
            : '#005249';

        // eslint-disable-next-line no-nested-ternary
        const labelColor = isProductCommission
          ? 'secondary'
          : row.commissionPlan?.type === 'Fixed'
            ? 'primary'
            : 'success';

        return (
          <Box display="flex" alignItems="center" height="100%" gap={1}>
            <BaseLabel color={labelColor}>
              {startCase(row.commissionPlan?.name) || '/'}
            </BaseLabel>
            {row.commissionPlan?.name && (
            <Iconify
              icon={planIcon}
              width={18}
              sx={{
                color: planColor,
              }}
            />
            )}
          </Box>
        );
      },
      valueGetter: (_, row) => startCase(row.commissionPlan?.name) || '/',
    },
    {
      field: 'userId',
      headerName: t('assignedTo', { ns: 'orders' }),
      width: 200,
      renderCell: ({ row }) => {
        const selectedMember = members.find(
          (member) => (member.email === row.member?.email),
        ) || { email: row.userId, name: `User ${row.userId}` };

        // Use the row.userId as fallback if no selectedMember is found
        const displayName = selectedMember ? startCase(selectedMember.name) : `User ${row.userId}`;

        return (
          <Box id="order-assigned-employee" display="flex" alignItems="center" height="100%">
            <FormProvider {...methods}>
              {ordersPermissions.isAllowManage ? (
                <AvatarSelect
                  onChange={(val) => handleMemberChange(row.id, val as TMember, selectedMember as TMember)}
                  options={members}
                  value={selectedMember as TMember}
                />
              ) : (
                <Typography variant="caption">
                  {displayName || '/'}
                </Typography>
              )}
            </FormProvider>
          </Box>
        );
      },
      valueGetter: (_, row) => startCase(row.member?.name) || row.userId,
    },
    {
      field: 'actions',
      type: 'actions',
      headerName: t('actions', { ns: 'common' }),
      disableExport: true,
      sortable: false,
      align: 'right',
      width: 200,
      renderCell: ({ row }) => {
        const orderChanges = changes[row.id];
        const shouldShowSaveButton = orderChanges?.memberChanged || orderChanges?.commissionPlanChanged;

        return (
          <Stack direction="row" spacing={1}>
            <Box
              sx={{
                cursor: 'pointer',
              }}
              pr={2}
              onClick={() => openProductExchanges(row.id)}
            >
              <Iconify
                sx={{
                  color: BASE_COLORS.primary,
                }}
                icon="carbon:ibm-data-product-exchange"
                width={24}
              />
            </Box>
            {shouldShowSaveButton && (
            <LoadingButton
              key={row.id}
              variant="contained"
              color="primary"
              loading={isReassignMappingLoading || isAssignMappingLoading}
              disabled={isReassignMappingLoading || isAssignMappingLoading}
              onClick={() => handleSave(row.id)}
            >
              {t('saveChanges', { ns: 'common' })}
            </LoadingButton>
            )}
          </Stack>
        );
      },
    },
  ];

  const openShopifyIdVideo = () => {
    posthog.capture('ManageMate_web', {
      shopName: personnel?.shopName,
      email: personnel?.email,
      addedAt: new Date().toISOString(),
      section: 'orders',
      action: 'user_opened_shopify_id_video',
    });

    dispatch(memberActions.setIsOpenShopifyIdVideo(true));
  };

  const renderTableFooter = () => (
    <>
      <Divider sx={{ borderStyle: 'dashed' }} />
      <Stack direction="row" alignItems="center" justifyContent="space-between">
        <Box
          className="cursor-pointer"
          display="flex"
          alignItems="center"
          onClick={openShopifyIdVideo}
          sx={{
            animation: `${blinkAnimation} 1s`,
            animationIterationCount: 5,
          }}
        >
          <Button
            sx={{
              ml: 2,
              mt: 2,
            }}
            variant="text"
            onClick={openShopifyIdVideo}
          >
            {t('howToAssignOrdersAutomatically', { ns: 'team' })}
          </Button>
        </Box>
        <OrdersCountLoader />
      </Stack>
    </>
  );

  return (
    <>
      <PrintWrapper componentRef={componentRef}>
        <div ref={resizableDiv}>
          <Card
            sx={{
              padding: 0,
              width: 'auto',
              px: 0,
              borderRadius: 0,
              pb: 8,
              // eslint-disable-next-line no-nested-ternary
              height: exporting ? 'auto' : (isFullTable && getAttributedOrdersByFilters?.length > 0 ? 'auto' : pageHeight !== 0 && !isPrintMode
                ? isDesktopSmallScreen ? 800 : pageHeight : 'auto'),
              ...(isMobile && {
                height: 'auto',
                background: 'unset',
                boxShadow: 'unset',
                px: 0,
                pt: 0,
                pb: '35px',
              }),
            }}
          >
            <div id="locations-filter">
              <MemoizedFilterTabs tabs={tabs} onFilterChange={handleFilterStatus} />
            </div>
            <DataGridPro
              rows={getAttributedOrdersByFilters}
              columns={orderColumns}
              apiRef={apiRef}
              slots={{
                toolbar: renderTableHeader,
                noRowsOverlay: renderCustomNoRowsOverlay,
                footer: renderTableFooter,
              }}
              showToolbar
              columnVisibilityModel={columnVisibilityModel}
              slotProps={{
                toolbar: {
                  showQuickFilter: true,
                  printOptions: {
                    hideFooter: true,
                    hideToolbar: true,
                  },
                },
              }}
              sx={{
                px: 0,
                '.MuiDataGrid-main > *:first-of-type': {
                  borderTopLeftRadius: 0,
                  borderTopRightRadius: 0,
                },
              }}
              localeText={localText}
              hideFooterPagination
              getRowId={(row) => row.id}
              className={isPrintMode ? 'print-mode' : ''}
            />
          </Card>
        </div>
      </PrintWrapper>
      <AssignShopifyIdDialog
        setIsOpenDialog={setIsOpenAssignDialog}
        updatedOrder={updatedOrder}
        isOpenDialog={isOpenAssignDialog}
      />
    </>
  );
};

export {
  OrdersList,
};
